import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";
import { computeSeason, DEFAULT_SEASON_SETTINGS, type Season } from "@/cms/lib/seasonalMedia";
import { publicRows } from "@/lib/publicBackend";

type SeasonSettings = {
  mode: "manual" | "auto";
  current: Season;
  summer_start: string;
  winter_start: string;
};

const QUERY_KEY = ["app_settings", "season"];

async function fetchSeasonSettings(): Promise<SeasonSettings> {
  const rows = await publicRows<{ value: Partial<SeasonSettings> }>(
    "app_settings",
    "select=value&key=eq.season&limit=1",
  );
  const v = rows[0]?.value ?? {};
  return { ...DEFAULT_SEASON_SETTINGS, ...v };
}

/** Read-only hook used by frontend components to pick summer/winter media. */
export function useSeason(): Season {
  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSeasonSettings,
    staleTime: 60_000,
  });
  return computeSeason(data ?? DEFAULT_SEASON_SETTINGS);
}

/** Backend hook with full settings + setter (writes to app_settings). */
export function useSeasonSettings() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSeasonSettings,
    staleTime: 30_000,
  });
  const mutation = useMutation({
    mutationFn: async (next: Partial<SeasonSettings>) => {
      const merged = { ...(query.data ?? DEFAULT_SEASON_SETTINGS), ...next };
      const { error } = await ((await getSupabase()) as any)
        .from("app_settings")
        .upsert({ key: "season", value: merged }, { onConflict: "key" });
      if (error) throw error;
      return merged;
    },
    onSuccess: (merged) => {
      qc.setQueryData(QUERY_KEY, merged);
    },
  });
  const settings = query.data ?? DEFAULT_SEASON_SETTINGS;
  return {
    settings,
    active: computeSeason(settings),
    isLoading: query.isLoading,
    update: mutation.mutateAsync,
    saving: mutation.isPending,
  };
}
